import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import AudioButton from '../components/AudioButton';
import { getImageUrl } from '../config';
import { FiGlobe, FiHome, FiHeart, FiAward, FiTarget, FiBook, FiStar, FiGift, FiUsers, FiTrendingUp, FiCoffee, FiCalendar, FiChevronRight, FiMic, FiCheckCircle } from 'react-icons/fi';

const CulturalApplications = ({ updateProgress, setCurrentSection }) => {
  const [activeTab, setActiveTab] = useState('family');
  const [activeScenario, setActiveScenario] = useState(0);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    setCurrentSection('cultural');
    updateProgress('cultural');
  }, []);

  const culturalContexts = {
    family: {
      title: 'Family Unity & Collective Identity',
      icon: FiHome,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        {
          title: 'The Power of "We"',
          description: 'In Ethiopian families, the collective "We" (Egna) often replaces "I" (Ene) to show that one\'s identity is tied to the family unit.',
          example: { am: 'የእኛ ቤት (Ye-egna bet)', en: 'Our house (instead of "My house")' },
          audioId: 'ye-egna-bet',
          image: getImageUrl('betachin.jpg'),
          culturalNote: 'This reflects the Ethiopian value of community over individualism.',
          icon: FiHeart,
          contextType: 'family-unity'
        },
        {
          title: 'Respect Through Language',
          description: 'Younger family members use honorifics when addressing elders, showing proper upbringing and cultural values.',
          example: { am: 'አባታችን እንዴት ናቸው? (Abatachin endet nachew?)', en: 'How is our father?' },
          audioId: 'abatachin-endet',
          image: getImageUrl('father.jpg'),
          culturalNote: 'Using "our father" instead of "my father" shows collective respect.',
          icon: FiAward,
          contextType: 'respect'
        },
        {
          title: 'Generational Wisdom',
          description: 'Elders speak to younger family members with authority and guidance, using formal language patterns.',
          example: { am: 'ልጄ ሁን (lijē huni)', en: 'Be my child (blessing/guidance)' },
          audioId: 'lije-huni',
          image: getImageUrl('elder-respect.jpg'),
          culturalNote: 'This formal address establishes the natural hierarchy within families.',
          icon: FiTarget,
          contextType: 'generational'
        }
      ]
    },
    religious: {
      title: 'Church & Religious Protocol',
      icon: FiBook,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      scenarios: [
        {
          title: 'Addressing Religious Figures',
          description: 'Priests and religious leaders are addressed with the highest honorifics, never using casual forms.',
          example: { am: 'አባታችን (Abatachin)', en: 'Our Father (Title for Priests)' },
          audioId: 'abatachin',
          image: getImageUrl('priest.jpg'),
          culturalNote: 'This shows collective reverence and respect for religious authority.',
          icon: FiBook,
          contextType: 'religious-respect'
        },
        {
          title: 'Formal Prayer Language',
          description: 'Prayers and religious ceremonies use the most formal language to show devotion and respect.',
          example: { am: 'አምላካችን እግዚአብሔር (Amlakachin Egziabher)', en: 'Our God' },
          audioId: 'amlakachin',
          image: getImageUrl('church-prayer.jpg'),
          culturalNote: 'Formal address in prayer reflects the sacred nature of communication with God.',
          icon: FiStar,
          contextType: 'prayer'
        },
        {
          title: 'Blessing & Benediction',
          description: 'Elders give blessings using formal language that conveys spiritual authority and tradition.',
          example: { am: 'እግዚአብሔር ይባርክህ (Egziabher yibarkeh)', en: 'May God bless you' },
          audioId: 'yibarkeh',
          image: getImageUrl('elder-respect.jpg'),
          culturalNote: 'The formal structure carries the weight of spiritual tradition.',
          icon: FiGift,
          contextType: 'blessing'
        }
      ]
    },
    social: {
      title: 'Social Mediation & Arbitration',
      icon: FiUsers,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      scenarios: [
        {
          title: 'Shimgilina (Arbitration)',
          description: 'During conflict resolution, language becomes very formal to maintain dignity and cool tempers.',
          example: { am: 'እርስዎ ምን ይላሉ? (Erswo min yilalu?)', en: 'What do you (honored) say?' },
          audioId: 'erswo-min',
          image: getImageUrl('arbitration.jpg'),
          culturalNote: 'Formal language creates distance and respect during difficult conversations.',
          icon: FiCheckCircle,
          contextType: 'arbitration'
        },
        {
          title: 'Community Council',
          description: 'Community meetings use formal pronouns to ensure all voices are heard with proper respect.',
          example: { am: 'እናንተ ምን ትላላችሁ? (Ennante min tilalachhu?)', en: 'What do you (all) say?' },
          audioId: 'ennante-min',
          image: getImageUrl('community-meeting.jpg'),
          culturalNote: 'Formal address maintains order and respect in community decision-making.',
          icon: FiUsers,
          contextType: 'council'
        },
        {
          title: 'Market Interactions',
          description: 'Even in commercial settings, proper pronoun use shows respect and builds trust.',
          example: { am: 'እርስዎ ምን ይፈልጋሉ? (Erswo min yifelegalu?)', en: 'What would you (formal) like?' },
          audioId: 'erswo-commerce',
          image: getImageUrl('market-scene.jpg'),
          culturalNote: 'Respectful language in commerce builds long-term customer relationships.',
          icon: FiTrendingUp,
          contextType: 'commerce'
        }
      ]
    },
    ceremony: {
      title: 'Coffee Ceremony & Hospitality',
      icon: FiCoffee,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      scenarios: [
        {
          title: 'Coffee Ceremony Protocol',
          description: 'The traditional coffee ceremony is a cornerstone of Ethiopian social life, where language and hospitality intertwine.',
          example: { am: 'ቡና እየተፈላ ነው (Bunna eyetefela new)', en: 'Coffee ceremony in progress' },
          audioId: 'bunna-eyetefela-new', // Updated to use ID
          image: getImageUrl('coffee-ceremony.jpg'),
          culturalNote: 'Coffee ceremony creates space for formal conversation and community bonding.',
          icon: FiCoffee,
          contextType: 'coffee-ceremony'
        },
        {
          title: 'Guest Welcome',
          description: 'Guests are honored with formal language that shows the host\'s respect and hospitality.',
          example: { am: 'እንኳን ደህና መጡ (Enkwan dehna metu)', en: 'Welcome (formal greeting)' },
          audioId: 'enkwan-dehna', // Updated to use ID
          image: getImageUrl('hospitality-food.jpg'),
          culturalNote: 'Formal welcome phrases honor the guest and the host-guest relationship.',
          icon: FiHeart,
          contextType: 'hospitality'
        },
        {
          title: 'Festival Gatherings',
          description: 'Cultural festivals use formal language to celebrate traditions and honor elders.',
          example: { am: 'መልካም በዓል (Melkam be\'al)', en: 'Happy holiday (formal)' },
          audioId: 'melkam-beal', // Updated to use ID
          image: getImageUrl('festival.jpg'),
          culturalNote: 'Festive language reinforces cultural continuity and respect for tradition.',
          icon: FiCalendar,
          contextType: 'festival'
        }
      ]
    }
  };

  const currentContext = culturalContexts[activeTab];
  const currentScenario = currentContext.scenarios[activeScenario];

  const getContextColor = (type) => {
    switch (type) {
      case 'family-unity': return 'text-green-600 bg-green-100';
      case 'respect': return 'text-purple-600 bg-purple-100';
      case 'generational': return 'text-blue-600 bg-blue-100';
      case 'religious-respect': return 'text-indigo-600 bg-indigo-100';
      case 'prayer': return 'text-purple-600 bg-purple-100';
      case 'blessing': return 'text-amber-600 bg-amber-100';
      case 'arbitration': return 'text-cyan-600 bg-cyan-100';
      case 'council': return 'text-blue-600 bg-blue-100';
      case 'commerce': return 'text-emerald-600 bg-emerald-100';
      case 'coffee-ceremony': return 'text-orange-600 bg-orange-100';
      case 'hospitality': return 'text-rose-600 bg-rose-100';
      case 'festival': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => { setActiveTab(id); setActiveScenario(0); }}
      className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${activeTab === id
        ? `bg-gradient-to-r ${culturalContexts[id].color} text-white shadow-md transform scale-105`
        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
        }`}
    >
      <SafeIcon icon={icon} />
      {label}
    </button>
  );

  const ScenarioCard = ({ scenario, index, isActive }) => (
    <div
      className={`relative cursor-pointer transition-all duration-300 ${isActive ? 'transform scale-105' : 'hover:transform hover:scale-102'}`}
      onClick={() => setActiveScenario(index)}
    >
      <div className={`bg-white rounded-[2rem] border-2 transition-all duration-300 overflow-hidden h-full flex flex-col p-4 ${isActive ? `border-purple-500 shadow-lg ${currentContext.bgColor}` : 'border-gray-200 hover:border-gray-300'}`}>
        {/* Image Section - OPTIMIZED: Rounded container inside card with object-cover (removed padding/contain) */}
        <div className="relative h-48 rounded-2xl overflow-hidden bg-gray-50 border-2 border-gray-100 shadow-inner flex items-center justify-center mb-2">
          <img src={scenario.image} alt={scenario.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
          
          {/* Context Type Badge - Rounded Rectangle */}
          <div className="absolute top-3 left-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium shadow-sm border border-black/5 ${getContextColor(scenario.contextType)}`}>
              <SafeIcon icon={scenario.icon} className="text-xs" />
              {scenario.contextType.replace('-', ' ')}
            </span>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8 rounded-b-xl">
            <h3 className="text-white font-bold text-lg mb-1 drop-shadow-md">{scenario.title}</h3>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-2 flex-1 flex flex-col">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">{scenario.description}</p>

          {/* Example Section */}
          <div className={`${currentContext.bgColor} rounded-xl p-4 border ${currentContext.borderColor} mt-auto`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Example</span>
              <AudioButton audioId={scenario.audioId} text={scenario.example.am} small />
            </div>
            <div className="font-amharic text-lg font-medium text-gray-800 mb-1">{scenario.example.am}</div>
            <div className="text-sm text-gray-600">{scenario.example.en}</div>
          </div>

          {/* Cultural Note */}
          <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex items-start gap-2">
              <SafeIcon icon={FiBook} className="text-blue-500 text-sm mt-0.5" />
              <p className="text-xs text-blue-700 leading-relaxed">{scenario.culturalNote}</p>
            </div>
          </div>
        </div>

        {/* Active Indicator */}
        {isActive && (
          <div className="absolute top-3 right-3">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center text-white shadow-lg animate-pulse">
              <SafeIcon icon={FiChevronRight} className="text-sm" />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Cultural Context"
      subtitle="Language doesn't exist in a vacuum. See how Amharic grammar comes alive in daily Ethiopian life."
      icon={FiGlobe}
      color="text-amber-700"
      bgColor="bg-amber-50"
      prevLink="/pronouns"
      prevLabel="Pronouns"
      nextLink="/exercises"
      nextLabel="Exercises"
    >
      <div className="space-y-8 fade-in">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-3 bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
          <TabButton id="family" label="Family" icon={FiHome} />
          <TabButton id="religious" label="Religious" icon={FiBook} />
          <TabButton id="social" label="Social" icon={FiUsers} />
          <TabButton id="ceremony" label="Ceremony" icon={FiCoffee} />
        </div>

        {/* Active Context Overview */}
        <div className={`bg-gradient-to-r ${currentContext.color} rounded-[2rem] p-8 text-white shadow-lg`}>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
              <SafeIcon icon={currentContext.icon} className="text-2xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{currentContext.title}</h2>
              <p className="text-white/90 leading-relaxed">
                Explore how {currentContext.title.toLowerCase()} shapes the use of pronouns and formal language in Ethiopian culture.
              </p>
            </div>
            <button
              onClick={() => setShowTip(!showTip)}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
              title="Toggle cultural insights"
            >
              <SafeIcon icon={FiMic} />
            </button>
          </div>
        </div>

        {/* Cultural Insight Tip */}
        {showTip && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-[2rem] p-6 border border-blue-200">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <SafeIcon icon={FiBook} className="text-blue-600 text-xl" />
              </div>
              <div>
                <h4 className="font-bold text-blue-900 mb-2">Cultural Insight</h4>
                <p className="text-blue-700 text-sm leading-relaxed">
                  In Ethiopian culture, the choice of pronouns goes beyond grammar—it's a dance of respect, hierarchy, and social harmony. Notice how different contexts (family, church, market) require different levels of formality, creating a rich tapestry of social communication.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Scenario Gallery */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-800">Real-Life Scenarios</h3>
            <span className="text-sm text-gray-500">Click to explore • {activeScenario + 1} of {currentContext.scenarios.length}</span>
          </div>

          {/* Grid of Scenario Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentContext.scenarios.map((scenario, index) => (
              <ScenarioCard key={index} scenario={scenario} index={index} isActive={activeScenario === index} />
            ))}
          </div>
        </div>

        {/* Expanded Active Scenario */}
        <div className="bg-white rounded-[2rem] shadow-lg border border-gray-200 overflow-hidden">
          {/* Expanded Image - OPTIMIZED: object-contain with rounded corners and padding */}
          <div className="relative h-96 bg-gray-50 flex items-center justify-center overflow-hidden p-6">
            <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
               <img src={currentScenario.image} alt={currentScenario.title} className="w-full h-full object-cover rounded-2xl" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
               <div className="absolute inset-x-0 bottom-0 p-8">
                <div className="text-white max-w-2xl">
                    <h2 className="text-3xl font-bold mb-2 drop-shadow-lg">{currentScenario.title}</h2>
                    <p className="text-white/90 text-lg drop-shadow-md">{currentScenario.description}</p>
                </div>
               </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiMic} className="text-purple-500" /> Language Example
                </h4>
                <div className={`${currentContext.bgColor} rounded-2xl p-6 border ${currentContext.borderColor}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-bold uppercase tracking-wider text-gray-500">Phrase</span>
                    <AudioButton audioId={currentScenario.audioId} text={currentScenario.example.am} />
                  </div>
                  <div className="font-amharic text-2xl font-bold text-gray-800 mb-2">
                    {currentScenario.example.am}
                  </div>
                  <div className="text-gray-600 mb-4">{currentScenario.example.en}</div>
                  <div className="flex items-center gap-2 px-3 py-1 bg-white/60 rounded-xl inline-block border border-black/5">
                    <SafeIcon icon={currentScenario.icon} className="text-sm" />
                    <span className="text-xs font-medium">{currentScenario.contextType.replace('-', ' ')}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiBook} className="text-blue-500" /> Cultural Context
                </h4>
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                  <p className="text-blue-700 leading-relaxed mb-4">{currentScenario.culturalNote}</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-600">Context: {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-600">Formality Level: High</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-gray-600">Social Impact: Significant</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cultural Proverb */}
        <div className="bg-gradient-to-r from-green-700 to-green-900 rounded-[2rem] p-8 text-white text-center shadow-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-white opacity-5 rounded-full -translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-yellow-400 opacity-5 rounded-full translate-x-16 translate-y-16"></div>
          <div className="max-w-3xl mx-auto relative z-10">
            <div className="mb-6 inline-block p-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <SafeIcon icon={FiUsers} className="text-4xl text-yellow-400" />
            </div>
            <h3 className="text-3xl font-amharic mb-2 font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-400">
              "ሰው ለሰው መድኃኒት ነው"
            </h3>
            <p className="text-green-200 italic mb-8 text-lg">"Sew le-sew medhanit new"</p>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20 text-left transform transition-all hover:bg-white/15 shadow-lg">
              <h4 className="text-xl font-bold text-yellow-400 mb-6 flex items-center gap-3 border-b border-white/10 pb-4">
                <span className="text-2xl">🌿</span>
                <span className="uppercase tracking-wider">Meaning</span>
              </h4>
              <div className="space-y-6 text-white/90 leading-relaxed text-lg">
                <p className="font-medium flex items-start gap-3">
                  <span className="text-yellow-400 mt-1.5">•</span>
                  It conveys the idea that human beings are each other’s source of healing, comfort, and support.
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-yellow-400 mt-1.5">•</span>
                  Just as medicine cures illness, kindness, empathy, and companionship cure loneliness, hardship, and suffering.
                </p>
                <div className="pt-4 mt-2 border-t border-white/10">
                  <p className="text-green-100 italic flex items-center gap-2">
                    <SafeIcon icon={FiHeart} className="text-yellow-400" />
                    It emphasizes the Ethiopian cultural value of mutual care and solidarity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CulturalApplications;